<script setup>
  import {computed, nextTick, onBeforeMount, reactive, ref} from "vue";

  import ComponentDosageInput from "@components/Inputs/DosageInput.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentTimeSelect from "@components/TimeSelect.vue";
  import {daysNumber} from "@enums/medicationEnums.js";

  defineOptions({
    name: "MedicationComponentDetailDialogSingleDosageInput",
  });

  const props = defineProps({
    dataHashId: {
      type: String,
      required: true,
    },
    dosageForm: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    intervalValue: {
      type: [String, Number],
      required: true,
    },
  });

  const emit = defineEmits(["isInvalid", "change", "isValid"]);

  const currentValue = ref(null);
  const currentValueAsFraction = ref(null);
  const time = ref(null);
  const weekday = ref(null);
  const dayOfMonth = ref(null);

  // computed instead of a value copied in onBeforeMount, so changing the
  // medication's unit above updates the unit shown next to the dosage input
  const unit = computed(() => props.dosageForm.selected_unit ?? "");

  const dataList = reactive([
    {
      dayofmonth: null,
      month: null,
      time: null,
      value: 1,
      value_as_fraction: false,
      weekday: null,
    },
  ]);

  onBeforeMount(() => {
    if (props.dosageForm.dosagealternative && props.dosageForm.dosagealternative.dosagealternativedatas) {
      dataList[0] = props.dosageForm.dosagealternative.dosagealternativedatas[0];
    }

    time.value = dataList[0].time ?? null;
    currentValue.value = dataList[0].value ?? null;
    currentValueAsFraction.value = dataList[0].value_as_fraction ?? false;
    weekday.value = dataList[0].weekday ?? null;
    dayOfMonth.value = dataList[0].dayofmonth ?? null;

    // immediately emit change event (the null values, which are
    // represented with "1", will always be stored)
    nextTick(() => {
      emit("change", {newData: dataList});
    });
  });

  const handleInput = ({as_fraction, id, value}) => {
    dataList[0].value = value;
    dataList[0].value_as_fraction = as_fraction;

    emit("isValid", id);
    emit("change", {newData: dataList});
  };

  const handleWeekDay = () => {
    dataList[0].weekday = weekday.value;

    emit("change", {newData: dataList});
  };

  const handleTimeInput = (newTime) => {
    dataList[0].time = newTime.time;

    emit("change", {newData: dataList});
  };
</script>

<template>
  <ul>
    <li class="flex items-baseline space-x-4">
      <ComponentDosageInput
        :id="'specialDosage' + dataHashId"
        :key="'specialDosage' + dataHashId"
        :name="'specialDosage' + dataHashId"
        :value="currentValue"
        :as-fraction="currentValueAsFraction"
        :test-id="getTestId('single')"
        @input="handleInput"
        @is-invalid="$emit('isInvalid', $event)"
      />

      <div class="truncate pt-2 text-sm">
        {{ unit }}
      </div>

      <template v-if="type === 'specialWeeks'">
        <ComponentSelect v-model="weekday" class="w-28!" :options="daysNumber" @change="handleWeekDay" />
      </template>

      <template v-if="!['multipleHours', 'specialWeeks', 'specialMonth'].includes(type)">
        <ComponentTimeSelect :value="time" @selected-time="handleTimeInput" />
      </template>
    </li>
  </ul>
</template>
